import { apiFetch, apiConfig } from '../config/api';
import { Choir } from '../types/Choir';

class ChoirService {
  private static readonly BASE_URL = '/api/choirs';
  private static readonly ADMIN_BASE_URL = '/api/admin/choirs';

  // Public endpoints (no authentication required)
  static async getAllActiveChoirs(): Promise<Choir[]> {
    return await apiFetch(this.BASE_URL);
  }

  static async getChoirById(id: string): Promise<Choir> {
    return await apiFetch(`${this.BASE_URL}/${id}`);
  }

  // Admin endpoints (authentication required)
  static async getAllChoirs(): Promise<Choir[]> {
    return await apiFetch(this.ADMIN_BASE_URL);
  }


  static async getChoirByIdAdmin(id: string): Promise<Choir> {
    return await apiFetch(`${this.ADMIN_BASE_URL}/${id}`);
  }

  static async createChoir(choir: Partial<Choir>): Promise<Choir> {
    return await apiFetch(this.ADMIN_BASE_URL, {
      method: 'POST',
      body: JSON.stringify(choir),
    });
  }

  static async updateChoir(id: string, choir: Partial<Choir>): Promise<Choir> {
    return await apiFetch(`${this.ADMIN_BASE_URL}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(choir),
    });
  }

  static async deleteChoir(id: string): Promise<void> {
    await apiFetch(`${this.ADMIN_BASE_URL}/${id}`, {
      method: 'DELETE',
    });
  }

  static async permanentlyDeleteChoir(id: string): Promise<void> {
    await apiFetch(`${this.ADMIN_BASE_URL}/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  // Image management methods
  static async uploadChoirImage(choirId: string, file: File): Promise<Choir> {
    console.log('=== CHOIR IMAGE UPLOAD DEBUG START ===');

    const formData = new FormData();
    formData.append('file', file);

    // Get token from user object in localStorage (same as apiFetch)
    const storedUser = localStorage.getItem('user');
    console.log('Raw stored user data:', storedUser);

    let token = null;
    let userData = null;
    try {
      if (storedUser) {
        userData = JSON.parse(storedUser);
        token = userData.token;
        console.log('Parsed user data:', {
          username: userData.username,
          role: userData.role,
          hasToken: !!userData.token,
          schoolId: userData.schoolId
        });
      } else {
        console.log('No user data found in localStorage');
      }
    } catch (err) {
      console.error('Error parsing user data from localStorage:', err);
    }

    // Debug logging
    console.log('Upload attempt details:', {
      choirId,
      hasToken: !!token,
      userRole: userData?.role,
      userName: userData?.username,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token',
      fileSize: file.size,
      fileName: file.name,
      fileType: file.type
    });

    // Use the centralized API configuration instead of hardcoded URL
    const url = `${apiConfig.baseURL}${this.ADMIN_BASE_URL}/${choirId}/images`;
    console.log('Upload URL:', url);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    console.log('Request headers:', headers);

    console.log('About to send request...');

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    console.log('Response received:');
    console.log('Response status:', response.status);
    console.log('Response statusText:', response.statusText);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        console.error('Server error response:', errorText);
      } catch (e) {
        console.error('Could not read error response:', e);
      }

      console.log('=== CHOIR IMAGE UPLOAD DEBUG END (FAILED) ===');
      throw new Error(`Failed to upload image: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
    }

    console.log('Upload successful!');
    console.log('=== CHOIR IMAGE UPLOAD DEBUG END (SUCCESS) ===');
    return await response.json();
  }

  static async deleteChoirImage(choirId: string, imageId: string): Promise<Choir> {
    return await apiFetch(`${this.ADMIN_BASE_URL}/${choirId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  static getImageDataUrl(choirId: string, imageId: string, contentType: string, base64Data: string): string {
    return `data:${contentType};base64,${base64Data}`;
  }
}

export default ChoirService;
